import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatCurrency, formatDate, formatDateLong } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Receipt, Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight, Loader2, Check, X, Send, Upload, FileText, ArrowLeft, ExternalLink, ScanLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  Draft: "bg-gray-100 text-gray-700 border-gray-300",
  Submitted: "bg-blue-50 text-blue-700 border-blue-200",
  Approved: "bg-green-50 text-green-700 border-green-200",
  Rejected: "bg-red-50 text-red-500 border-red-200",
};

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [selected, setSelected] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set("search", search);
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (categoryFilter && categoryFilter !== "all") params.set("category_id", categoryFilter);
      const res = await api.get(`/expenses?${params}`);
      setExpenses(res.data.expenses);
      setTotalPages(res.data.total_pages);
      setStats(res.data.stats);
    } catch { toast.error("Failed to load expenses"); }
    setLoading(false);
  }, [search, statusFilter, categoryFilter, page]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { api.get("/expense-categories").then((r) => setCategories(r.data.categories)); }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/expenses/${deleteId}`);
      toast.success("Expense deleted");
      setDeleteId(null);
      fetchExpenses();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleAction = async (id, action, data = {}) => {
    try {
      await api.post(`/expenses/${id}/${action}`, data);
      toast.success(`Expense ${action === "submit" ? "submitted" : action === "approve" ? "approved" : action === "reject" ? "rejected" : "updated"}`);
      fetchExpenses();
      if (detailId) {
        const res = await api.get(`/expenses/${detailId}`);
        setDetailId(null);
        setTimeout(() => setDetailId(res.data.expense_id), 50);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleBulkApprove = async () => {
    try {
      const res = await api.post("/expenses/bulk-approve", { expense_ids: selected });
      toast.success(res.data.message);
      setSelected([]);
      fetchExpenses();
    } catch { toast.error("Bulk approve failed"); }
  };

  const toggleSelect = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const totalExpenses = expenses.length;

  return (
    <div data-testid="expenses-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Expenses</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <Badge className="bg-gray-100 text-gray-600 rounded-full text-xs">{stats.draft || 0} Draft</Badge>
            <Badge className="bg-blue-50 text-blue-700 rounded-full text-xs">{stats.submitted || 0} Submitted</Badge>
            <Badge className="bg-green-50 text-green-700 rounded-full text-xs">{stats.approved || 0} Approved</Badge>
            {(stats.rejected || 0) > 0 && <Badge className="bg-red-50 text-red-500 rounded-full text-xs">{stats.rejected} Rejected</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && isAdmin && (
            <Button data-testid="bulk-approve-btn" variant="outline" size="sm" onClick={handleBulkApprove} className="rounded-sm text-green-600 border-green-200">
              <Check className="w-3.5 h-3.5 mr-1" /> Approve {selected.length}
            </Button>
          )}
          <Button data-testid="create-expense-btn" onClick={() => setCreateOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            <Plus className="w-4 h-4 mr-1" /> New Expense
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="expense-search" placeholder="Search expenses..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 rounded-sm border-gray-300" />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[150px] rounded-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {["Draft", "Submitted", "Approved", "Rejected"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoryFilter || "all"} onValueChange={(v) => { setCategoryFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[170px] rounded-sm"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.filter((c) => c.active).map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-200 rounded-sm shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 bg-gray-50/50">
              {isAdmin && <th className="p-3 w-10"><input type="checkbox" checked={selected.length > 0 && selected.length === expenses.filter((e) => e.status === "Submitted").length} onChange={() => { const sub = expenses.filter((e) => e.status === "Submitted").map((e) => e.expense_id); setSelected(selected.length === sub.length ? [] : sub); }} className="rounded border-gray-300" /></th>}
              <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Expense #</th>
              <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Vendor</th>
              <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Category</th>
              <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
              <th className="p-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Amount</th>
              <th className="p-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
              <th className="p-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isAdmin ? 8 : 7} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></td></tr>
              ) : expenses.length === 0 ? (
                <tr><td colSpan={isAdmin ? 8 : 7} className="p-8 text-center text-gray-500">No expenses found</td></tr>
              ) : expenses.map((exp) => (
                <tr key={exp.expense_id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                  {isAdmin && <td className="p-3">{exp.status === "Submitted" && <input type="checkbox" checked={selected.includes(exp.expense_id)} onChange={() => toggleSelect(exp.expense_id)} className="rounded border-gray-300" />}</td>}
                  <td className="p-3 cursor-pointer" onClick={() => setDetailId(exp.expense_id)}>
                    <span className="font-mono font-semibold text-[#E63946] hover:underline text-sm">{exp.expense_number}</span>
                    {exp.linked_po_number && <span className="text-[10px] text-gray-400 ml-1">({exp.linked_po_number})</span>}
                  </td>
                  <td className="p-3 text-gray-700 max-w-[150px] truncate">{exp.vendor || "—"}</td>
                  <td className="p-3 text-gray-500 text-xs">{exp.category_name || "—"}</td>
                  <td className="p-3 text-gray-500 text-xs">{formatDate(exp.expense_date)}</td>
                  <td className="p-3 text-right font-semibold text-sm">{formatCurrency(exp.amount)}</td>
                  <td className="p-3"><Badge className={`${statusColors[exp.status] || ""} text-xs rounded-full border px-2 py-0.5`}>{exp.status}</Badge></td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {exp.status === "Draft" && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={() => handleAction(exp.expense_id, "submit")} title="Submit"><Send className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteId(exp.expense_id)}><Trash2 className="w-3 h-3" /></Button>
                        </>
                      )}
                      {exp.status === "Submitted" && isAdmin && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleAction(exp.expense_id, "approve")} title="Approve"><Check className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => { setRejectDialog(exp); setRejectReason(""); }} title="Reject"><X className="w-3.5 h-3.5" /></Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-sm"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-sm"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {createOpen && <CreateExpenseDialog open={createOpen} categories={categories} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); fetchExpenses(); }} />}

      {detailId && <ExpenseDetailDialog expenseId={detailId} categories={categories} isAdmin={isAdmin} onClose={() => setDetailId(null)}
        onAction={(action, data) => handleAction(detailId, action, data)}
        onRefresh={fetchExpenses} />}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Expense?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Reject {rejectDialog?.expense_number}?</AlertDialogTitle>
          <AlertDialogDescription>
            <textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..." className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none mt-2 focus:outline-none focus:ring-2 focus:ring-[#E63946]" />
          </AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { handleAction(rejectDialog.expense_id, "reject", { reason: rejectReason }); setRejectDialog(null); }} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Reject</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateExpenseDialog({ open, categories, onClose, onCreated }) {
  const [form, setForm] = useState({
    vendor: "", amount: "", category_id: "", category_name: "",
    expense_date: new Date().toISOString().split("T")[0],
    description: "", receipts: [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    const formData = new FormData();
    for (let f of files) formData.append("files", f);
    try {
      const res = await api.post("/upload-multiple", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, receipts: [...prev.receipts, ...res.data.files] }));
      toast.success(`${res.data.files.length} file(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleScanReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setOcrResult(null);

    // Upload first
    const uploadForm = new FormData();
    uploadForm.append("files", file);
    try {
      const uploadRes = await api.post("/upload-multiple", uploadForm, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, receipts: [...prev.receipts, ...uploadRes.data.files] }));
    } catch { /* continue with OCR even if upload fails */ }

    // OCR extract
    const ocrForm = new FormData();
    ocrForm.append("file", file);
    try {
      const res = await api.post("/ocr/extract", ocrForm, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data;
      if (d.success) {
        setOcrResult(d);
        setForm((prev) => ({
          ...prev,
          vendor: d.vendor_name || prev.vendor,
          amount: d.total_amount ? String(d.total_amount) : prev.amount,
          expense_date: d.date || prev.expense_date,
          description: prev.description || (d.invoice_number ? `Invoice ${d.invoice_number}` : ""),
          supplier_vat_number: d.vat_number || prev.supplier_vat_number || "",
          subtotal_excl_vat: d.subtotal ? String(d.subtotal) : prev.subtotal_excl_vat || "",
          total_vat: d.vat_amount ? String(d.vat_amount) : prev.total_vat || "",
          vat_code: d.vat_category || prev.vat_code || "standard",
        }));
        toast.success(`Receipt scanned — ${d.confidence} confidence`);
      } else {
        toast.error(d.error || "OCR failed. Enter details manually.");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "OCR not available. Enter details manually.";
      toast.error(msg);
    }
    setScanning(false);
  };

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    if (form.description.length < 10) { toast.error("Description must be at least 10 characters"); return; }
    setSaving(true);
    try {
      await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Expense created");
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-sm">
        <DialogHeader><DialogTitle className="font-heading text-lg">New Expense</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* OCR Scan Section */}
          <div className="border-2 border-dashed border-blue-200 bg-blue-50/50 rounded-sm p-3">
            <label className="flex items-center justify-center gap-2 cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-900">
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
              {scanning ? "Analyzing receipt..." : "Scan Receipt / Invoice (auto-fill with AI)"}
              <input data-testid="ocr-scan-input" type="file" accept="image/*,.pdf" onChange={handleScanReceipt} className="hidden" disabled={scanning} />
            </label>
          </div>

          {ocrResult && (
            <div data-testid="ocr-result-badge" className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-sm border ${
              ocrResult.confidence === "high" ? "bg-green-50 text-green-700 border-green-200" :
              ocrResult.confidence === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" :
              "bg-red-50 text-red-600 border-red-200"}`}>
              <ScanLine className="w-3.5 h-3.5" />
              OCR: {ocrResult.confidence_score}% confidence — {ocrResult.confidence}
              {ocrResult.warnings?.length > 0 && <span className="ml-1 text-gray-500">({ocrResult.warnings.join(", ")})</span>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Vendor</label>
              <Input data-testid="expense-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Vendor name" className="rounded-sm" /></div>
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Amount (R) *</label>
              <Input data-testid="expense-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="rounded-sm" /></div>
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Category</label>
              <Select value={form.category_id || "_none"} onValueChange={(v) => {
                const cat = categories.find((c) => c.category_id === v);
                setForm({ ...form, category_id: v === "_none" ? "" : v, category_name: cat?.name || "" });
              }}>
                <SelectTrigger data-testid="expense-category" className="rounded-sm"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No Category</SelectItem>
                  {categories.filter((c) => c.active).map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Date</label>
              <Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="rounded-sm" /></div>
          </div>
          <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Description *</label>
            <textarea data-testid="expense-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe this expense (min 10 chars)..." className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946]" /></div>
          {/* VAT Section */}
          <div className="border border-gray-200 rounded-sm p-3 space-y-3">
            <p className="text-xs font-semibold uppercase text-gray-500">VAT Information</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-medium text-gray-500 block mb-0.5">Supplier VAT Number</label>
                <Input data-testid="expense-supplier-vat" value={form.supplier_vat_number || ""} onChange={(e) => setForm({ ...form, supplier_vat_number: e.target.value })} placeholder="4987654321" maxLength={10} className="rounded-sm h-8 text-xs" /></div>
              <div><label className="text-[10px] font-medium text-gray-500 block mb-0.5">VAT Code</label>
                <select data-testid="expense-vat-code" value={form.vat_code || "standard"} onChange={(e) => setForm({ ...form, vat_code: e.target.value })}
                  className="w-full rounded-sm border border-gray-300 px-2 py-1 text-xs h-8 focus:outline-none focus:ring-2 focus:ring-[#E63946]">
                  <option value="standard">Standard (15%)</option>
                  <option value="zero-rated">Zero-Rated (0%)</option>
                  <option value="exempt">Exempt</option>
                </select></div>
              <div><label className="text-[10px] font-medium text-gray-500 block mb-0.5">Subtotal (Excl VAT)</label>
                <Input data-testid="expense-subtotal" type="number" step="0.01" value={form.subtotal_excl_vat || ""} onChange={(e) => {
                  const sub = parseFloat(e.target.value) || 0;
                  const rate = form.vat_code === "standard" ? 15 : 0;
                  const vat = Math.round(sub * rate) / 100;
                  setForm({ ...form, subtotal_excl_vat: e.target.value, total_vat: String(vat), amount: String(Math.round((sub + vat) * 100) / 100) });
                }} placeholder="0.00" className="rounded-sm h-8 text-xs" /></div>
              <div><label className="text-[10px] font-medium text-gray-500 block mb-0.5">VAT Amount</label>
                <Input data-testid="expense-vat-amount" type="number" step="0.01" value={form.total_vat || ""} onChange={(e) => setForm({ ...form, total_vat: e.target.value })} placeholder="0.00" className="rounded-sm h-8 text-xs" /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="expense-input-vat-claimable" type="checkbox" checked={form.input_vat_claimable !== false}
                onChange={(e) => setForm({ ...form, input_vat_claimable: e.target.checked })}
                className="rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]" />
              <span className="text-xs text-gray-700">Input VAT claimable</span>
            </label>
            {form.input_vat_claimable !== false && (
              <div className="w-1/2"><label className="text-[10px] font-medium text-gray-500 block mb-0.5">Claim Percentage (%)</label>
                <Input data-testid="expense-claim-pct" type="number" min="0" max="100" value={form.input_vat_claim_percentage ?? 100} onChange={(e) => setForm({ ...form, input_vat_claim_percentage: e.target.value })} className="rounded-sm h-8 text-xs" />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Receipts / Invoices</label>
            <div className="border border-dashed border-gray-300 rounded-sm p-3">
              <label className="flex items-center justify-center gap-2 cursor-pointer text-sm text-gray-500 hover:text-[#E63946]">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload files (PDF, Images)"}
                <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleFileUpload} className="hidden" />
              </label>
              {form.receipts.length > 0 && (
                <div className="mt-2 space-y-1">
                  {form.receipts.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded-sm px-2 py-1">
                      <FileText className="w-3 h-3" /> {r.filename}
                      <Button variant="ghost" size="icon" className="h-4 w-4 ml-auto" onClick={() => setForm({ ...form, receipts: form.receipts.filter((_, j) => j !== i) })}><X className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="save-expense-btn" onClick={handleSubmit} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Create Expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseDetailDialog({ expenseId, categories, isAdmin, onClose, onAction, onRefresh }) {
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/expenses/${expenseId}`).then((r) => setExpense(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [expenseId]);

  if (loading || !expense) return (
    <Dialog open onOpenChange={onClose}><DialogContent className="rounded-sm"><div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div></DialogContent></Dialog>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {expense.expense_number}
            <Badge className={`${statusColors[expense.status] || ""} text-xs rounded-full border`}>{expense.status}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-400 text-xs uppercase block">Vendor</span><span className="font-medium">{expense.vendor || "—"}</span></div>
            <div><span className="text-gray-400 text-xs uppercase block">Amount</span><span className="font-bold text-[#E63946] text-lg">{formatCurrency(expense.amount)}</span></div>
            <div><span className="text-gray-400 text-xs uppercase block">Category</span><span>{expense.category_name || "—"}</span></div>
            <div><span className="text-gray-400 text-xs uppercase block">Date</span><span>{formatDateLong(expense.expense_date)}</span></div>
            <div className="col-span-2"><span className="text-gray-400 text-xs uppercase block">Description</span><p className="text-gray-700 mt-0.5">{expense.description}</p></div>
            <div><span className="text-gray-400 text-xs uppercase block">Created by</span><span>{expense.created_by_name}</span></div>
            {expense.approved_by_name && <div><span className="text-gray-400 text-xs uppercase block">Approved by</span><span>{expense.approved_by_name}</span></div>}
          </div>

          {expense.linked_po_number && (
            <div className="bg-gray-50 rounded-sm p-3 flex items-center justify-between">
              <div className="text-sm"><span className="text-gray-400 text-xs uppercase block">Linked Purchase Order</span>
                <span className="font-mono font-semibold text-[#E63946]">{expense.linked_po_number}</span></div>
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => { onClose(); navigate(`/purchase-orders/${expense.linked_po_id}`); }}>
                <ExternalLink className="w-3 h-3 mr-1" /> View PO
              </Button>
            </div>
          )}

          {expense.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3">
              <p className="text-xs font-semibold text-red-700">Rejection Reason:</p>
              <p className="text-sm text-red-600 mt-0.5">{expense.rejection_reason}</p>
            </div>
          )}

          {expense.receipts?.length > 0 && (
            <div>
              <span className="text-gray-400 text-xs uppercase block mb-1">Receipts</span>
              <div className="space-y-1">
                {expense.receipts.map((r, i) => (
                  <a key={i} href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-blue-600 hover:underline bg-blue-50 rounded-sm px-2 py-1">
                    <FileText className="w-3 h-3" /> {r.filename}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex gap-2">
          {expense.status === "Draft" && (
            <Button size="sm" onClick={() => { onAction("submit"); onClose(); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"><Send className="w-3.5 h-3.5 mr-1" /> Submit</Button>
          )}
          {expense.status === "Submitted" && isAdmin && (
            <>
              <Button size="sm" onClick={() => { onAction("approve"); onClose(); }} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
              <Button size="sm" variant="outline" onClick={() => { onAction("return-to-draft"); onClose(); }} className="rounded-sm text-gray-600"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Return to Draft</Button>
            </>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
